import { Container, Button, Box, Typography, CircularProgress } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowBack } from '@mui/icons-material';
import { CreateManualInvestmentRequestBody } from '@inversor-hub/types';
import { useCreateManualInvestment, useUploadInvestmentMainImage } from '@/lib/api/investments.ts';
import { GoogleMapsProvider } from '@/context/GoogleMapsProvider.tsx';
import InvestmentForm from '@/features/investments/form/components/InvestmentForm.tsx';
import UpgradeWallModal from '@/features/billing/components/UpgradeWallModal.tsx';
import { useInvestmentForm } from '@/features/investments/form/useInvestmentForm.ts';
import useSnackbar from '@/hooks/useSnackbar.tsx';
import { useAnalytics } from '@/hooks/useAnalytics.ts';
import { ANALYTICS_PLATFORM, trackProductEvent } from '@/lib/analytics/product-events';
import { useInvestorInvestmentAccess } from '@/features/billing/useInvestorInvestmentAccess.ts';

type CreateInvestmentLocationState = {
  entryPoint: 'dashboard' | 'portfolio' | 'empty_state' | 'ai' | 'direct';
};

function getEntryPointFromState(entryPoint: unknown): CreateInvestmentLocationState['entryPoint'] {
  if (
    entryPoint === 'dashboard' ||
    entryPoint === 'portfolio' ||
    entryPoint === 'empty_state' ||
    entryPoint === 'ai'
  ) {
    return entryPoint;
  }

  return 'direct';
}

const CreateInvestmentPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const createInvestment = useCreateManualInvestment();
  const uploadMainImage = useUploadInvestmentMainImage();
  const {
    canCreateInvestment,
    isLoading: isLoadingAccess,
    upgradeWallInvestmentCount,
    upgradeWallPlansPath,
  } = useInvestorInvestmentAccess();
  const { show: showSnackbar } = useSnackbar();
  const { trackEvent } = useAnalytics();
  const entryPoint = getEntryPointFromState(
    (location.state as CreateInvestmentLocationState)?.entryPoint
  );

  const { initialFormData, handleSubmit, handleErrorDismiss, error, isLoading } = useInvestmentForm(
    {
      onSubmit: async ({ payload, mainImageFile }) => {
        const investment = await createInvestment.mutateAsync(
          payload as CreateManualInvestmentRequestBody
        );

        if (mainImageFile && investment?.id) {
          await uploadMainImage.mutateAsync({ investmentId: investment.id, file: mainImageFile });
        }

        trackProductEvent(trackEvent, 'investment_created', {
          investment_id: investment.id,
          investment_type: investment.type,
          entry_point: entryPoint,
          role: 'investor',
          platform: ANALYTICS_PLATFORM,
        });
      },
      onSuccess: () => {
        navigate('/inversiones');
        showSnackbar({ title: 'Inversión registrada', variant: 'success' });
      },
    }
  );

  const handleCancel = () => {
    navigate('/inversiones');
  };

  if (isLoadingAccess) {
    return (
      <Container maxWidth="md" sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <GoogleMapsProvider>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <UpgradeWallModal
          open={!canCreateInvestment}
          onClose={handleCancel}
          onViewPlans={() => navigate(upgradeWallPlansPath)}
          investmentCount={upgradeWallInvestmentCount}
        />
        <Box sx={{ mb: 3 }}>
          <Button startIcon={<ArrowBack />} onClick={handleCancel} sx={{ mb: 2 }}>
            Volver a inversiones
          </Button>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            Nueva inversión
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Registra una nueva inversión inmobiliaria en tu portafolio
          </Typography>
        </Box>

        {canCreateInvestment ? (
          <InvestmentForm
            initialData={initialFormData}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            submitLabel="Crear inversión"
            loadingLabel="Guardando..."
            isLoading={isLoading}
            error={error}
            onErrorDismiss={handleErrorDismiss}
          />
        ) : null}
      </Container>
    </GoogleMapsProvider>
  );
};

export default CreateInvestmentPage;
